import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatAmount } from '../money';
import { ReportStatusBadge } from './ReportStatusBadge';
import type { Report, SubmitBlocker } from './types';

/** The three totals as figures (#952 M15): in the header, on every status. */
function Totals({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  const figures = [
    ['total', report.total],
    ['reimbursable', report.reimbursable],
    ['company', report.companyPaid],
  ] as const;
  return (
    <dl className="flex flex-wrap gap-x-8 gap-y-2" data-testid="report-totals">
      {figures.map(([key, amount]) => (
        <div key={key} className="flex flex-col">
          <dt className="text-muted-foreground text-xs">{t(`reports.page.totals.${key}`)}</dt>
          <dd className="text-foreground text-lg font-semibold tabular-nums">
            {formatAmount(amount)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The note's header bar (#952 H5, H6, M15): number and title, the status beside them, the three
 * totals and the main actions, in the same place on every status. From 620 px of shell width it
 * stays at the top while the page scrolls; on a phone it scrolls away and the approver's decision
 * has its own bottom bar.
 */
export function ReportHeaderBar({
  report,
  actions,
  notices,
}: {
  readonly report: Report;
  /** The main action and its neighbours, at the bar's end. */
  readonly actions: ReactNode;
  /** What explains the actions (a blocked «Invia», a PDF in progress), under the totals. */
  readonly notices?: ReactNode;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div
      data-testid="report-header-bar"
      className="bg-card border-border z-10 flex flex-col gap-3 border-b pb-3 @min-[620px]/shell:sticky @min-[620px]/shell:top-0 @min-[620px]/shell:pt-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-muted-foreground text-xs tracking-wide uppercase">
            {t('reports.page.kind')}
          </span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 id="report-title" className="screen-title">
              {report.number} · {report.title}
            </h2>
            <ReportStatusBadge status={report.status} stage={report.approvalStage} />
          </div>
        </div>
        {actions}
      </div>
      <Totals report={report} />
      {notices}
    </div>
  );
}

const LINK =
  'text-link inline-flex min-h-11 items-center font-semibold underline underline-offset-3 @min-[620px]/shell:min-h-0';

/** One reason «Invia» is off, with the step that removes it as a link (#952 M16). */
function Reason({ report, blocker }: { readonly report: Report; readonly blocker: SubmitBlocker }) {
  const { t } = useTranslation('expenses');
  if (blocker.code === 'OWNER_TAX_CODE_MISSING') {
    // The owner completes their own profile; Gestione, on someone else's note, that person's data.
    const own = report.can?.owned === true;
    const name = report.owner.displayName;
    return (
      <span className="text-muted-foreground flex flex-wrap items-center gap-x-1 text-sm">
        {t(`reports.blockerText.OWNER_TAX_CODE_MISSING_${own ? 'own' : 'other'}`, { name })}
        <Link
          className={LINK}
          to={
            own ? '/profile' : `/expenses/people?person=${encodeURIComponent(report.owner.userId)}`
          }
        >
          {own ? t('reports.blockerLink.profile') : t('reports.blockerLink.person', { name })}
        </Link>
      </span>
    );
  }
  return (
    <span className="text-muted-foreground flex flex-wrap items-center gap-x-1 text-sm">
      {t(`reports.blockers.${blocker.code}`)}
      {blocker.expenseId ? (
        <Link className={LINK} to={`/expenses/${encodeURIComponent(blocker.expenseId)}`}>
          {t('reports.blockerLink.expense')}
        </Link>
      ) : null}
    </span>
  );
}

/** The reasons «Invia per approvazione» is off: one per code, the first expense it names. */
export function SubmitReasons({
  report,
  blockers,
  id,
}: {
  readonly report: Report;
  readonly blockers: readonly SubmitBlocker[];
  readonly id: string;
}) {
  if (blockers.length === 0) return null;
  const first = new Map<string, SubmitBlocker>();
  for (const blocker of blockers) if (!first.has(blocker.code)) first.set(blocker.code, blocker);
  return (
    <div id={id} className="flex flex-col gap-1" data-testid="submit-reasons">
      {[...first.values()].map((blocker) => (
        <Reason key={blocker.code} report={report} blocker={blocker} />
      ))}
    </div>
  );
}
